import React, { useState } from 'react';
import { LayoutDashboard, Upload, Settings, Info, Link2 } from 'lucide-react';
import ActualCost2Settings from './ActualCost2Settings'; // CHA-323 phase 2
import ActualCost2Uploads from './ActualCost2Uploads'; // CHA-323 phase 3
import ActualCost2Dashboard from './ActualCost2Dashboard'; // CHA-323 phase 4
import ActualCost2Links from './ActualCost2Links'; // CHA-356 — invoice↔job link overrides

/**
 * ActualCost2 — the new Import Data → "Actual Cost 2" module (CHA-323).
 *
 * The productized version of the CHA-316 duty/freight redesign, validated live 2026-07-16
 * (chain proven; duty/tariff penny-exact vs the old process; freight source identified).
 * It runs ALONGSIDE the existing Actual Cost workflow so the two can be compared before cutover.
 *
 * Data model — everything joins on the broker job# (B-series):
 *   received (ERP) --comm_inv--> List 1 (invoice→job) --job#--> { List 2 freight, ACE duty+tariff }
 *   - List 1: C-Air "US Import Invoice Line Report" (commercial invoice → job#)  → lookup_broker_invoice
 *   - List 2: C-Air "List of Forwarding Invoices" (job# → ocean + air freight, + container)
 *   - ACE:    imports_data_source_1 (job# → duty + tariff; hts 99xx = tariff, real HTS = duty)
 *
 * Sub-nav: Dashboard (actual-vs-system reconciliation), Uploads (dual-coast broker files),
 * Settings (warehouse→coast + scope). Each is its own component.
 */

const SUB_TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'links', label: 'Links', icon: Link2 }, // CHA-356
  { id: 'uploads', label: 'Uploads', icon: Upload },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export default function ActualCost2({ userProfile, tenantId }) {
  const [tab, setTab] = useState('dashboard');

  return (
    <div className="space-y-4">
      {/* Sub-nav */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="flex border-b border-slate-200">
          {SUB_TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-teal-600 text-teal-700 bg-teal-50/50'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Intent banner — the validated pipeline, so reviewers/operator see the shape */}
      <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-4">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-teal-700 mt-0.5 shrink-0" />
          <div className="text-xs text-slate-700 leading-relaxed">
            <span className="font-semibold text-teal-800">Actual Cost 2</span> reconciles what the ERP
            <em> accrued</em> against what was <em>actually paid</em>, by shipment and style — duty + tariff from
            ACE, freight from the forwarding-invoice list, all linked to your commercial invoices via the broker
            job#. Runs alongside the existing Actual Cost process for side-by-side comparison.
          </div>
        </div>
      </div>

      {tab === 'dashboard' && <ActualCost2Dashboard userProfile={userProfile} tenantId={tenantId} />}

      {tab === 'links' && <ActualCost2Links tenantId={tenantId} />}

      {tab === 'uploads' && <ActualCost2Uploads tenantId={tenantId} />}

      {tab === 'settings' && <ActualCost2Settings userProfile={userProfile} />}
    </div>
  );
}
